'use client'

import { useRef } from 'react'
import Link from 'next/link'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { useStackRecede } from './useStackRecede'
import { ClientBrowser, ClientLogo } from './ClientParts'
import '../styles/shipped-evidence.css'
import '../styles/client-cards.css'

// A client's card on the industry pages: their live homepage in a browser
// frame (opens the case study, when there is one), logo, what the business
// does and, for projects the owner has written up, what we built with a link
// to the case study. Only "View website" goes to the client's own site.
// Cards come from lib/clientCards.js and stack like the Shipped Evidence
// cards on wide screens.

function ClientCard({ card }) {
  const { story } = card
  return (
    <>
      <ClientBrowser card={card} href={story?.href} />
      <div className="cl-body">
        <div className="cl-id">
          <ClientLogo card={card} />
          <div>
            <h3 className="cl-name">{card.name}</h3>
            <span className="cl-kind">{card.kind}</span>
          </div>
        </div>
        <p className="cl-about">{card.about}</p>

        {story ? (
          <div className="cl-built">
            <span className="se-adapt-badge">What we built</span>
            <p className="cl-built-title"><Link href={story.href}>{story.title}</Link></p>
            <p className="cl-built-text">{story.summary}</p>
            {story.tags.length ? (
              <div className="se-tags">
                {story.tags.map((tag) => <span key={tag} className="se-tag">{tag}</span>)}
              </div>
            ) : null}
          </div>
        ) : (
          <span className="se-adapt-badge cl-badge">{card.ghl ? 'Built on GoHighLevel' : 'Client project'}</span>
        )}

        <div className="cl-actions">
          {story ? (
            <Link href={story.href} className="cl-btn is-primary">
              Read the case study <ArrowRight size={15} aria-hidden="true" />
            </Link>
          ) : null}
          {card.url ? (
            <a href={card.url} target="_blank" rel="noopener noreferrer" className="cl-btn" aria-label={`View ${card.name}'s website (opens in a new tab)`}>
              View website <ExternalLink size={13} aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default function ClientStack({ items }) {
  const listRef = useRef(null)
  useStackRecede(listRef, items)
  if (!items?.length) return null
  return (
    <div className="se-pairs cl-stack" ref={listRef}>
      {items.map((card, index) => (
        <article
          key={card.id}
          className="se-pair cl-card"
          data-rv="0.3"
          style={{ '--i': index, '--rv-y': '46px', '--rv-dur': '.75s' }}
        >
          <ClientCard card={card} />
        </article>
      ))}
    </div>
  )
}
